import { nextTick, onMounted, reactive, ref } from "vue";
import { track } from "./analytics.js";
import { buildSignupPayload } from "./payload.js";
import {
  COURSE_NAME,
  PRIVACY_CONTACT_EMAIL,
  PRIVACY_CONTROLLER,
  PRIVACY_INSTAGRAM,
} from "./privacy.js";
import { loadWaitlistStatus } from "./status-loader.js";
import { createSubmitController } from "./submit-adapter.js";
import { NAME_MAX, validateConsentValue, validateEmailValue, validateNameValue } from "./validation.js";

const DRAFT_KEYS = ["alabastra-waitlist-draft-v1", "alabastra-waitlist-draft-v2"];

function escapeHtml(value) {
  return String(value)
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;");
}

export default {
  name: "WaitlistApp",
  setup() {
    const consentVersion = ref("3");
    const company = ref("");
    const name = ref("");
    const email = ref("");
    const whatsapp = ref(false);
    const consent = ref(false);
    const accepted = ref(false);
    const formStatus = ref("");
    const formError = ref("");
    const showSubmitRetry = ref(false);
    const submitInFlight = ref(false);
    const errors = reactive({ email: "", name: "", consent: "" });
    const termosDialog = ref(null);
    const prontoRef = ref(null);
    const submitController = createSubmitController();

    function clearDraftStorage() {
      try {
        for (const key of DRAFT_KEYS) {
          localStorage.removeItem(key);
        }
      } catch {
        // storage may be blocked
      }
    }

    // The form renders straight away with the default consent version; the
    // status call only refreshes it, so a failed call never blocks a sign-up.
    async function refreshStatus() {
      const result = await loadWaitlistStatus();
      if (result.ok) {
        consentVersion.value = result.consentVersion;
      }
    }

    function clearMessages() {
      formStatus.value = "";
      formError.value = "";
      showSubmitRetry.value = false;
    }

    function focusFirstInvalid() {
      if (errors.email) {
        document.getElementById("wl-email")?.focus();
      } else if (errors.name) {
        document.getElementById("wl-name")?.focus();
      } else if (errors.consent) {
        document.getElementById("wl-consent")?.focus();
      }
    }

    function validateForm() {
      errors.email = validateEmailValue(email.value);
      errors.name = validateNameValue(name.value);
      errors.consent = validateConsentValue(consent.value);
      if (errors.email) {
        focusFirstInvalid();
        return false;
      }
      if (errors.name) {
        focusFirstInvalid();
        return false;
      }
      if (errors.consent) {
        focusFirstInvalid();
        return false;
      }
      return true;
    }

    const signupHandlers = {
      onPending: (msg) => {
        formStatus.value = msg;
        formError.value = "";
        showSubmitRetry.value = false;
      },
      onSuccess: () => {
        clearMessages();
        accepted.value = true;
        track("sign_up", { method: "waitlist" });
        nextTick(() => prontoRef.value?.focus());
      },
      onError: (message, meta) => {
        formStatus.value = "";
        formError.value = message;
        showSubmitRetry.value = Boolean(meta?.retryable);
        if (meta?.field === "email") {
          errors.email = message;
          document.getElementById("wl-email")?.focus();
        } else if (meta?.field === "consent") {
          errors.consent = message;
          document.getElementById("wl-consent")?.focus();
        }
      },
    };

    function submitSignup() {
      if (submitController.isInFlight() || accepted.value) {
        return;
      }
      clearMessages();
      if (!validateForm()) {
        return;
      }

      const built = buildSignupPayload(email.value, {
        name: name.value,
        whatsapp: whatsapp.value,
        consent: consent.value,
        consentVersion: consentVersion.value,
        company: company.value,
        source: "web",
      });
      if (!built.ok) {
        if (built.field === "email") errors.email = built.message;
        if (built.field === "name") errors.name = built.message;
        if (built.field === "consent") errors.consent = built.message;
        focusFirstInvalid();
        return;
      }

      submitInFlight.value = true;
      submitController.submit(built.payload, signupHandlers).finally(() => {
        submitInFlight.value = false;
      });
    }

    function onFormSubmit(event) {
      event.preventDefault();
      track("waitlist_submit_click");
      submitSignup();
    }

    /**
     * Retry rebuilds the request from the form as it is now, so a corrected
     * name or e-mail is sent and consent is checked again.
     */
    function retrySubmit() {
      submitSignup();
    }

    function openTermos(event) {
      event.preventDefault();
      event.stopPropagation();
      const dialog = termosDialog.value;
      if (dialog && typeof dialog.showModal === "function") {
        dialog.showModal();
      } else {
        dialog?.setAttribute("open", "");
      }
    }

    function closeTermos() {
      termosDialog.value?.close();
    }

    function onTermosClick(event) {
      if (event.target === termosDialog.value) {
        closeTermos();
      }
    }

    onMounted(() => {
      clearDraftStorage();
      refreshStatus();
    });

    const contactHtml = PRIVACY_CONTACT_EMAIL
      ? `<a href="mailto:${escapeHtml(PRIVACY_CONTACT_EMAIL)}">${escapeHtml(PRIVACY_CONTACT_EMAIL)}</a>`
      : '<span class="pendente">[e-mail de contato]</span>';

    return {
      company,
      name,
      email,
      whatsapp,
      consent,
      accepted,
      formStatus,
      formError,
      showSubmitRetry,
      submitInFlight,
      errors,
      termosDialog,
      prontoRef,
      NAME_MAX,
      PRIVACY_CONTROLLER,
      PRIVACY_INSTAGRAM,
      PRIVACY_CONTACT_EMAIL,
      COURSE_NAME,
      contactHtml,
      refreshStatus,
      onFormSubmit,
      retrySubmit,
      openTermos,
      closeTermos,
      onTermosClick,
      trackHeroClick() {
        track("cta_click", { location: "hero" });
      },
      clearFieldError(field) {
        errors[field] = "";
      },
    };
  },
  template: `
    <div class="page">
      <a class="skip-link" href="#titulo">Ir para o formulário</a>

      <header>
        <div class="wrap">
          <img
            class="logo-marca"
            src="/assets/alabastra-wordmark-brand.svg"
            alt="Alabastra"
          />
        </div>
      </header>

      <main>
        <figure class="foto">
          <picture>
            <source srcset="/assets/laboratorio.webp" type="image/webp" />
            <img
              src="/assets/laboratorio.jpg"
              alt="Laboratório de perfumaria à luz quente, com estante de frascos âmbar, bancada com béqueres e fitas olfativas e um caderno aberto (imagem ilustrativa)"
              width="844"
              height="1500"
              decoding="async"
              fetchpriority="high"
            />
          </picture>
        </figure>

        <div class="wrap">
          <section class="curso" aria-labelledby="titulo">
            <img
              class="marca-d-agua"
              src="/assets/alabastra-emblem-brand.svg"
              alt=""
              aria-hidden="true"
            />

            <a class="botao-lista" href="#titulo" @click="trackHeroClick">Entre na lista de espera</a>
            <p class="lead">Um curso de fundamentos para quem quer ir além de apenas “gostar” de perfume.</p>
            <div class="intro">
              <p>A perfumaria vai muito além dos milhares de perfumes lançados a cada ano. Por trás das fragrâncias há história, fundamentos e muito conhecimento.</p>
              <p>Entender de perfume é a proposta do primeiro curso da Alabastra: faremos a ponte entre gostar de perfume e conhecer o que existe por trás dele. As aulas são online, divididas em módulos, e o lançamento é no início de 2027.</p>
              <p>Ao longo das aulas, você vai conhecer as origens da perfumaria, entender o que são notas, acordes e famílias olfativas, aprender a descrever um perfume e descobrir o porquê das tendências.</p>
              <p>Ao final, além do certificado, você terá repertório para falar de perfumaria com a linguagem de quem entende do assunto.</p>
              <h1 id="titulo">Lista de <em>espera</em></h1>
              <p>As inscrições abrem em novembro, e as aulas começam no início de 2027.</p>
              <p>Entre agora na lista de espera. Quem estiver nela fica sabendo antes quando as inscrições abrirem.</p>
            </div>
            <p class="destaque">Os primeiros da lista terão preço especial no dia do lançamento.</p>

            <div
              class="honeypot"
              aria-hidden="true"
              style="position:absolute;left:-9999px;width:1px;height:1px;overflow:hidden"
            >
              <input
                id="company"
                v-model="company"
                name="company"
                type="text"
                tabindex="-1"
                autocomplete="off"
                aria-label="company"
              />
            </div>

            <form
              v-if="!accepted"
              class="form-curto"
              novalidate
              @submit="onFormSubmit"
            >
              <div class="campo" :class="{ invalido: errors.name }">
                <label class="so-leitor" for="wl-name">Seu nome</label>
                <input
                  id="wl-name"
                  v-model="name"
                  type="text"
                  autocomplete="name"
                  placeholder="Seu nome"
                  :maxlength="NAME_MAX"
                  :aria-invalid="errors.name ? 'true' : undefined"
                  :aria-describedby="errors.name ? 'wl-name-error' : undefined"
                  @input="clearFieldError('name')"
                />
                <p v-if="errors.name" id="wl-name-error" class="erro" role="alert">{{ errors.name }}</p>
              </div>

              <div class="campo" :class="{ invalido: errors.email }">
                <label class="so-leitor" for="wl-email">Seu e-mail</label>
                <input
                  id="wl-email"
                  v-model="email"
                  type="email"
                  inputmode="email"
                  autocomplete="email"
                  autocapitalize="off"
                  spellcheck="false"
                  placeholder="Seu e-mail"
                  required
                  :aria-invalid="errors.email ? 'true' : undefined"
                  :aria-describedby="errors.email ? 'wl-email-error' : undefined"
                  @input="clearFieldError('email')"
                />
                <p v-if="errors.email" id="wl-email-error" class="erro" role="alert">{{ errors.email }}</p>
              </div>

              <label class="opcao">
                <input id="wl-whatsapp" v-model="whatsapp" type="checkbox" />
                <span>Quero participar também do grupo de WhatsApp, onde trocamos informações e dicas de perfume (somente para inscritos).</span>
              </label>

              <label class="consent" :class="{ invalido: errors.consent }">
                <input
                  id="wl-consent"
                  v-model="consent"
                  type="checkbox"
                  :aria-invalid="errors.consent ? 'true' : undefined"
                  :aria-describedby="errors.consent ? 'wl-consent-error' : undefined"
                  @change="clearFieldError('consent')"
                />
                <span>
                  Tenho 18 anos ou mais, li e concordo com os
                  <a href="#termos" @click="openTermos">termos</a>.
                </span>
              </label>
              <p v-if="errors.consent" id="wl-consent-error" class="erro" role="alert">{{ errors.consent }}</p>

              <p
                v-if="formStatus"
                id="waitlist-form-status"
                class="live-message"
                role="status"
                aria-live="polite"
                aria-atomic="true"
              >{{ formStatus }}</p>
              <p
                v-if="formError"
                id="waitlist-form-error"
                class="live-message erro"
                role="alert"
                aria-live="assertive"
                aria-atomic="true"
              >{{ formError }}</p>
              <button
                v-if="showSubmitRetry"
                type="button"
                class="enviar enviar--inline"
                @click="retrySubmit"
              >
                Tentar novamente
              </button>

              <button type="submit" class="enviar" :disabled="submitInFlight">
                Quero entrar na lista
              </button>
            </form>

            <div v-else class="pronto" ref="prontoRef" tabindex="-1">
              <h2>Pronto, você está na lista!</h2>
              <p>Obrigada por querer entender perfume com a gente.</p>
            </div>
          </section>
        </div>
      </main>

      <footer>
        <div class="wrap">
          <a
            class="instagram"
            href="https://www.instagram.com/alabastra.perfume/"
            target="_blank"
            rel="noopener"
            :aria-label="'Alabastra no Instagram (' + PRIVACY_INSTAGRAM + ')'"
          >
            <svg viewBox="0 0 24 24" width="28" height="28" aria-hidden="true" focusable="false">
              <rect x="3" y="3" width="18" height="18" rx="5" fill="none" stroke="currentColor" stroke-width="1.8" />
              <circle cx="12" cy="12" r="4.2" fill="none" stroke="currentColor" stroke-width="1.8" />
              <circle cx="17.3" cy="6.7" r="1.2" fill="currentColor" />
            </svg>
          </a>
          <a :href="'mailto:' + PRIVACY_CONTACT_EMAIL">{{ PRIVACY_CONTACT_EMAIL }}</a>
        </div>
      </footer>

      <dialog
        id="termos"
        ref="termosDialog"
        aria-labelledby="titulo-termos"
        @click="onTermosClick"
      >
        <div class="termos-cab">
          <h2 id="titulo-termos">Termos da lista de espera</h2>
          <button type="button" aria-label="Fechar termos" @click="closeTermos">Fechar</button>
        </div>
        <p>
          Ao entrar na lista, você declara ter 18 anos ou mais e autoriza a Alabastra a usar seu nome e seu e-mail para:
        </p>
        <ul>
          <li>avisar sobre o curso {{ COURSE_NAME }} e a condição da turma fundadora;</li>
          <li>enviar conteúdos sobre perfumaria.</li>
        </ul>
        <p>
          Seus dados não são usados para nenhuma outra finalidade, nem vendidos ou cedidos a ninguém.
        </p>
        <p>
          Você pode cancelar quando quiser e pedir acesso, correção ou exclusão dos seus dados a qualquer momento, escrevendo para
          <span v-html="contactHtml"></span>
          ou pelo Instagram {{ PRIVACY_INSTAGRAM }}.
        </p>
        <p>
          Responsável pelos dados: {{ PRIVACY_CONTROLLER }}.
        </p>
      </dialog>
    </div>
  `,
};
